import type {
  CanvasContent,
  DiagramContent,
  MermaidContent,
} from '@/lib/api/diagrams.js';

import { loadMermaid } from './mermaid-loader.js';

/** What a thumbnail shows: a drawing, or the fact that there is nothing to draw. */
export type ThumbnailDrawing =
  { status: 'empty' } | { status: 'ready'; svg: string };

/**
 * Makes an SVG fill its box at its own aspect ratio. The size moves into the viewBox, the element
 * takes the full width and height, and `meet` centres the drawing without stretching it.
 */
export function fitSvgElement(svgElement: SVGSVGElement): void {
  const width = Number(svgElement.getAttribute('width'));
  const height = Number(svgElement.getAttribute('height'));
  if (!svgElement.getAttribute('viewBox') && width > 0 && height > 0) {
    svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
  svgElement.setAttribute('width', '100%');
  svgElement.setAttribute('height', '100%');
  svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svgElement.style.removeProperty('max-width');
}

async function drawMermaid(
  content: MermaidContent,
  renderId: string,
): Promise<ThumbnailDrawing> {
  if (content.source.trim().length === 0) return { status: 'empty' };
  const mermaid = await loadMermaid();
  const result = await mermaid.render(renderId, content.source);
  const parsed = new DOMParser().parseFromString(result.svg, 'text/html');
  const svgElement = parsed.querySelector('svg');
  if (!svgElement) throw new Error('mermaid returned no drawing');
  fitSvgElement(svgElement);
  return { status: 'ready', svg: svgElement.outerHTML };
}

/**
 * A whiteboard drawn with Excalidraw's exporter, not its editor: mounting the editor for every card
 * would be far heavier than the picture. Deleted shapes stay in the document as tombstones and are
 * left out here. Fonts are not inlined, so a gallery never downloads font files for its cards.
 */
async function drawCanvas(content: CanvasContent): Promise<ThumbnailDrawing> {
  const liveElements = content.elements.filter(
    (element) => (element as { isDeleted?: boolean }).isDeleted !== true,
  );
  if (liveElements.length === 0) return { status: 'empty' };
  const { exportToSvg } = await import('@excalidraw/excalidraw');
  const svgElement = await exportToSvg({
    elements: liveElements as never,
    files: (content.files ?? null) as never,
    appState: {
      exportBackground: true,
      viewBackgroundColor: content.settings.viewBackgroundColor ?? '#ffffff',
    },
    skipInliningFonts: true,
  });
  fitSvgElement(svgElement);
  return { status: 'ready', svg: svgElement.outerHTML };
}

/**
 * Draws a document for the gallery. Throws when it cannot, including for a content kind this build
 * does not know, and the card shows that as an unavailable preview.
 */
export async function drawThumbnail(
  content: DiagramContent,
  renderId: string,
): Promise<ThumbnailDrawing> {
  if (content.kind === 'mermaid') return drawMermaid(content, renderId);
  if (content.kind === 'canvas') return drawCanvas(content);
  throw new Error('unknown content kind');
}
